import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import Icon from '@components/Icon';
import { Button, Slider, Radio, InputNumber, Tooltip, Popover } from 'antd';
import ColorPicker from '@components/ColorPicker';
import stores from '@stores';
import backgroundConfig from '@utils/backgroundConfig';
import { cn } from '@utils/utils';
import SizeBar from './SizeBar';
import CropperImage from './CropperImage';
import Watermark from './Watermark';
import DownloadBar from './DownloadBar';
import DrawerBar from './DrawerBar';
import FrameBar from './FrameBar';
import ImageFilter from './ImageFilter';
import { captureScreen } from '@utils/captureScreen';
import useSetImg from '@hooks/useSetImg';

// 位置调整选项，直接从 Position 组件中提取
const positionCols = ['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right'];

export default observer(() => {
    const [showMore, setShowMore] = useState(false);
    const [positionOpen, setPositionOpen] = useState(false);
    const getFile = useSetImg(stores);
    
    const onCapture = async () => {
        const dataURL = await captureScreen();
        if (!dataURL) return;
        getFile(dataURL, 'dataURL');
    }
    
    const onBgChange = (e) => {
        const key = e.target.value;
        stores.option.setBackground(key);
    }
    
    // 位置调整的处理函数，直接从 Position 组件中提取
    const handlePositionChange = (newOpen) => {
        setPositionOpen(newOpen);
    };
    
    const handlePositionSelect = (value) => {
        stores.option.setAlign(value);
        setPositionOpen(false);
    }
    
    // 位置调整内容，直接从 Position 组件中提取
    const positionContent = (
        <div className={cn("flex flex-wrap w-24 position-block", stores.option.align)}>
            {positionCols.map(item => (
                <div key={item} className="w-8 h-8 border border-[var(--c-br)] rounded-sm hover:bg-[var(--c-wb)] cursor-pointer" onClick={() => handlePositionSelect(item)}></div>
            ))}
        </div>
    )
    
    return (
        <div className="bg-white dark:bg-gray-900 flex flex-col w-[340px] border-l border-gray-200 dark:border-gray-700 dark:text-gray-300 shadow-sm relative z-10 select-none">
            <div className="flex-1 flex-col gap-4 p-4 overflow-y-auto overflow-x-hidden">
                {/* 画布属性区域 */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                    <h3 className="text-sm font-bold mb-3 text-purple-600 dark:text-purple-400 flex items-center">
                        <Icon.Monitor size={16} useFa={true} className="mr-2" />
                        画布属性
                    </h3>
                    
                <SizeBar />
                
                    {/* 画布背景 */}
                    <div className="[&_label]:font-semibold [&_label]:text-sm mt-4">
                    <div className="flex justify-between items-center">
                        <label className="flex items-center">
                            <Icon.Palette size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                            画布背景
                        </label>
                        <Button
                            type="text"
                            size="small"
                            className="text-xs flex items-center opacity-80 m-0"
                            onClick={() => setShowMore(true)}
                        >更多 <Icon.ChevronRight size={16} /></Button>
                    </div>
                        <div className="flex flex-wrap gap-2 items-center justify-start py-2">
                            <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                onClick={() => {
                                    setShowMore(true);
                                    // 打开抽屉并切换到纯色标签页
                                    setTimeout(() => document.querySelector('.ant-tabs-tab[data-node-key="1"]')?.click(), 100);
                                }}
                            >
                                <Icon.Pipette size={20} useFa={true} className="mb-1 text-blue-500 dark:text-blue-400" />
                                <span className="text-xs">纯色</span>
                            </Button>
                            
                            <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                onClick={() => {
                                    setShowMore(true);
                                    // 打开抽屉并切换到渐变标签页
                                    setTimeout(() => document.querySelector('.ant-tabs-tab[data-node-key="2"]')?.click(), 100);
                                }}
                            >
                                <Icon.Palette size={20} useFa={true} className="mb-1 text-purple-500 dark:text-purple-400" />
                                <span className="text-xs">渐变</span>
                            </Button>
                            
                            <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                onClick={() => {
                                    setShowMore(true);
                                    // 打开抽屉并切换到纹理标签页
                                    setTimeout(() => document.querySelector('.ant-tabs-tab[data-node-key="3"]')?.click(), 100);
                                }}
                            >
                                <Icon.LayoutGrid size={20} useFa={true} className="mb-1 text-amber-500 dark:text-amber-400" />
                                <span className="text-xs">纹理</span>
                            </Button>
                            
                            <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                onClick={() => {
                                    setShowMore(true);
                                    // 打开抽屉并切换到图片标签页
                                    setTimeout(() => document.querySelector('.ant-tabs-tab[data-node-key="4"]')?.click(), 100);
                                }}
                            >
                                <Icon.ImagePlus size={20} useFa={true} className="mb-1 text-emerald-500 dark:text-emerald-400" />
                                <span className="text-xs">图片</span>
                            </Button>
                        </div>
                    </div>
                    
                    {/* 设备框架 - 移动到画布属性卡片内 */}
                    <div className="mt-4">
                        <FrameBar />
                    </div>
                </div>
                
                {/* 下面创建图片属性区域*/}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700 shadow-sm mt-4">
                    <h3 className="text-sm font-bold mb-3 text-purple-600 dark:text-purple-400 flex items-center">
                        <Icon.ImagePlus size={16} useFa={true} className="mr-2" />
                        图片属性
                    </h3>
                
                <div className="[&_label]:font-semibold pt-2 [&_label]:text-sm">
                    <label className="flex items-center">
                        <Icon.Settings2 size={16} useFa={true} className="mr-2 text-purple-500 dark:text-purple-400" />
                        快捷工具
                    </label>
                    <div className="flex flex-wrap gap-2 items-center justify-start py-2">
                            <Tooltip title="截取桌面窗口">
                                <Button 
                                    type="default" 
                                    className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                    onClick={onCapture}
                                >
                                    <Icon.Camera size={20} useFa={true} className="mb-1 text-blue-500 dark:text-blue-400" />
                                    <span className="text-xs">截取窗口</span>
                                </Button>
                            </Tooltip>
                        
                        <Tooltip title="裁剪图片">
                            <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                onClick={() => {
                                    // 保留原有CropperImage组件的点击行为
                                    document.querySelector('.crop-button-trigger')?.click();
                                }}
                            >
                                <Icon.Crop size={20} useFa={true} className="mb-1 text-indigo-500 dark:text-indigo-400" />
                                <span className="text-xs">裁剪</span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="水平翻转">
                        <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                            onClick={() => stores.option.toggleFlip('x')}
                            >
                                <Icon.FlipHorizontal2 size={20} useFa={true} className="mb-1 text-blue-500 dark:text-blue-400" />
                                <span className="text-xs">水平翻转</span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="垂直翻转">
                            <Button
                                type="default"
                                className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                                onClick={() => stores.option.toggleFlip('y')}
                            >
                                <Icon.FlipVertical2 size={20} useFa={true} className="mb-1 text-emerald-500 dark:text-emerald-400" />
                                <span className="text-xs">垂直翻转</span>
                            </Button>
                        </Tooltip>

                            <ImageFilter />

                        <Popover
                            content={positionContent}
                            trigger='click'
                            arrow={false}
                            placement="bottomRight"
                            overlayClassName={cn("shoteasy-components", stores.editor.isDark && 'dark-mode')}
                            open={positionOpen}
                            onOpenChange={handlePositionChange}
                        >
                            <Tooltip title="位置调整">
                                <Button
                                    type="default"
                                    className={cn("flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]", positionOpen && "shadow-md border-purple-400 dark:border-purple-500")}
                                >
                                    <Icon.LayoutGrid size={20} useFa={true} className="mb-1 text-amber-500 dark:text-amber-400" />
                                    <span className="text-xs">位置调整</span>
                                </Button>
                            </Tooltip>
                        </Popover>
                        
                        {/* 隐藏原始的裁剪组件但保留功能 */}
                        <div className="hidden">
                            <CropperImage />
                        </div>
                    </div>
                </div>
                    
                    {/* 图片控制选项卡片 */}
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700 shadow-sm mt-4">
                {/* 旋转控制 */}
                <div className="[&_label]:font-semibold [&_label]:text-sm">
                    <div className="flex justify-between items-center">
                        <label className="flex items-center">
                            <Icon.RotateCcw size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                            图片旋转
                        </label>
                        <InputNumber
                            size="small"
                            min={0}
                            max={359}
                            value={stores.option.rotation}
                            onChange={(value) => stores.option.setRotation(value)}
                            addonAfter="°"
                            className="w-20"
                        />
                    </div>
                    <div className="flex items-center py-2">
                        <Slider
                            min={0}
                            max={359}
                            step={1}
                            value={stores.option.rotation}
                            onChange={(value) => stores.option.setRotation(value)}
                            className="flex-1 mx-1"
                        />
                        <Button
                            type='text'
                            shape='circle'
                            onClick={() => stores.option.setRotation(0)}
                            icon={<Icon.RotateCcw size={16} />}
                            title="重置旋转"
                        />
                    </div>
                </div>
                        <div className="[&_label]:font-semibold [&_label]:text-sm mt-3">
                    <label className="flex items-center">
                        <Icon.ZoomIn size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                        缩放比例
                    </label>
                    <Slider
                        min={0.1}
                        max={3}
                        step={0.1}
                        onChange={(e) => stores.option.setScale(e)}
                        value={typeof stores.option.scale === 'number' ? stores.option.scale : 1}
                    />
                </div>
                        <div className="[&_label]:font-semibold [&_label]:text-sm mt-3">
                    <div className="flex justify-between">
                        <label className="flex items-center">
                            <Icon.Box size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                            内边距
                        </label>
                        <ColorPicker value={stores.option.paddingBg} onChange={(e) => stores.option.setPaddingBg(e.toRgbString())} size="small" />
                    </div>
                    <Slider
                        min={0}
                        max={60}
                        onChange={(e) => stores.option.setPadding(e)}
                        value={typeof stores.option.padding === 'number' ? stores.option.padding : 0}
                    />
                </div>
                        <div className="[&_label]:font-semibold [&_label]:text-sm mt-3">
                    <label className="flex items-center">
                        <Icon.Circle size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                        圆角
                    </label>
                    <Slider
                        min={0}
                        max={20}
                        onChange={(e) => stores.option.setRound(e)}
                        value={typeof stores.option.round === 'number' ? stores.option.round : 0}
                    />
                </div>
                        <div className="[&_label]:font-semibold [&_label]:text-sm mt-3">
                    <label className="flex items-center">
                        <Icon.Sun size={16} className="mr-2 text-purple-500 dark:text-purple-400" />
                        阴影
                    </label>
                    <Slider
                        min={0}
                        max={6}
                        onChange={(e) => stores.option.setShadow(e)}
                        value={typeof stores.option.shadow === 'number' ? stores.option.shadow : 0}
                    />
                </div>
                    </div>
                </div>
                
                {/* 水印区域 - 单独卡片 */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700 shadow-sm mt-4">
                    <h3 className="text-sm font-bold mb-3 text-purple-600 dark:text-purple-400 flex items-center">
                        <Icon.ImageDown size={16} useFa={true} className="mr-2" />
                        水印设置
                    </h3>
                    
                <Watermark />
                </div>
            </div>
            <DownloadBar />
            <DrawerBar showMore={showMore} onChange={setShowMore} />
        </div>
    )
});